import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { 
  ArrowLeft, Send, Loader2, Users, Brain, MessageSquare, 
  Briefcase, MapPin, GraduationCap, DollarSign, Home, Lightbulb,
  Paperclip, FileText, X
} from "lucide-react";
import PersonaCard from "@/components/PersonaCard";
import PersonaSkeleton from "@/components/PersonaSkeleton";
import QueryResultView from "@/components/QueryResultView";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useLanguage } from "@/i18n/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DashboardPageSimple() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useLanguage(); // Add language here!
  
  const [group, setGroup] = useState(null);
  const [personas, setPersonas] = useState([]);
  const [totalExpected, setTotalExpected] = useState(location.state?.expectedCount || 0); // Track expected count
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  
  const [query, setQuery] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [isQuerying, setIsQuerying] = useState(false);
  const [queryResults, setQueryResults] = useState([]);
  const [activeTab, setActiveTab] = useState("personas");
  
  // Future prediction state
  const [yearsIntoFuture, setYearsIntoFuture] = useState(0);
  const [scenarioType, setScenarioType] = useState("normal");
  const [showFutureScenario, setShowFutureScenario] = useState(null);

  // SIMPLE: Just poll every 2 seconds and fetch everything
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/target-groups/${groupId}`);
        setGroup(res.data.group);
        setPersonas(res.data.personas);
        setTotalExpected(res.data.group?.agent_count || 0); // Track expected
        
        const withAvatars = res.data.personas.filter(p => p.avatar_base64).length;
        console.log(`✅ Loaded: ${res.data.personas.length} personas (${withAvatars} with avatars)`);
      } catch (error) {
        console.error("Error:", error);
      }
    };
    
    // Fetch immediately
    fetchData();
    
    // Then poll every 2 seconds
    const interval = setInterval(fetchData, 2000);
    
    return () => clearInterval(interval);
  }, [groupId]);
  
  // Fetch queries once
  useEffect(() => {
    axios.get(`${API}/target-groups/${groupId}/queries`)
      .then(res => setQueryResults(res.data))
      .catch(console.error);
  }, [groupId]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedFile(file);
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => setFilePreview(reader.result);
        reader.readAsDataURL(file);
      } else {
        setFilePreview(null);
      }
    }
  };
  
  const removeFile = () => {
    setUploadedFile(null);
    setFilePreview(null);
  };

  const handleQuery = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    setIsQuerying(true);
    try {
      let requestData = { 
        question: query,
        years_into_future: yearsIntoFuture,
        scenario_type: yearsIntoFuture > 0 ? scenarioType : null
      };
      
      if (uploadedFile) {
        const reader = new FileReader();
        const fileBase64 = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(uploadedFile);
        });
        
        requestData.file_data = fileBase64;
        requestData.file_type = uploadedFile.type;
        requestData.file_name = uploadedFile.name;
      }
      
      const response = await axios.post(`${API}/target-groups/${groupId}/query`, requestData);
      setQueryResults(prev => [response.data, ...prev]);
      setQuery("");
      setUploadedFile(null);
      setFilePreview(null);
      setActiveTab("results");
      toast.success(t("dashboard.queryCompleted"));
    } catch (error) {
      console.error("Error querying:", error);
      toast.error(t("dashboard.queryFailed"));
    } finally {
      setIsQuerying(false);
    }
  };

  const handlePersonaClick = (persona) => {
    setSelectedPersona(persona);
    setIsDetailOpen(true);
  };

  const getTranslatedValue = (category, value) => {
    if (!value) return '';
    const translationKey = `${category}.${value}`;
    const translated = t(translationKey);
    if (translated === translationKey) return value;
    return translated;
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="absolute inset-0 hero-gradient pointer-events-none" />

      {/* Navigation */}
      <nav className="relative z-10 flex items-center justify-between px-6 md:px-12 py-6 border-b border-white/5">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-yellow-500 to-amber-500 flex items-center justify-center">
              <Brain className="w-5 h-5 text-black" />
            </div>
            <span className="font-heading font-semibold text-lg text-white">Behaviour Brain</span>
          </div>
          {group && (
            <div className="hidden md:flex items-center gap-2 text-zinc-500">
              <span>/</span>
              <span className="text-zinc-300">{group.name}</span>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <Button
            variant="ghost"
            onClick={() => navigate("/setup")}
            className="text-zinc-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t("nav.newStudy")}
          </Button>
        </div>
      </nav>

      <main className="relative z-10 px-6 md:px-12 py-8">
        {/* Query Interface */}
        <div className="mb-8">
          <form onSubmit={handleQuery} className="space-y-3">
            {uploadedFile && (
              <div className="glass rounded-xl p-3 flex items-center gap-3">
                {filePreview ? (
                  <img src={filePreview} alt="Upload" className="w-16 h-16 object-cover rounded-lg" />
                ) : (
                  <div className="w-16 h-16 bg-zinc-800 rounded-lg flex items-center justify-center">
                    <FileText className="w-6 h-6 text-zinc-500" />
                  </div>
                )}
                <div className="flex-1">
                  <p className="text-sm text-white font-medium">{uploadedFile.name}</p>
                  <p className="text-xs text-zinc-500">{(uploadedFile.size / 1024).toFixed(1)} KB</p>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={removeFile} className="text-zinc-400 hover:text-white">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
            
            {/* Future Timeline Selector */}
            <div className="glass rounded-2xl p-4 mb-3">
              <div className="flex items-center gap-4 mb-3">
                <span className="text-sm font-medium text-white">{t("future.title")}</span>
                <span className="text-xs text-zinc-500">
                  {yearsIntoFuture === 0 ? t("future.now") : `${new Date().getFullYear() + yearsIntoFuture} (+${yearsIntoFuture} ${t("future.years")})`}
                </span>
              </div>
              
              {/* Timeline Slider */}
              <div className="relative mb-4">
                <input
                  type="range"
                  min="0"
                  max="200"
                  value={yearsIntoFuture}
                  onChange={(e) => setYearsIntoFuture(parseInt(e.target.value))}
                  className="w-full h-2 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-yellow-500"
                  style={{
                    background: `linear-gradient(to right, #eab308 0%, #eab308 ${yearsIntoFuture / 2}%, #27272a ${yearsIntoFuture / 2}%, #27272a 100%)`
                  }}
                />
                <div className="flex justify-between mt-2 px-1">
                  <span className="text-xs text-zinc-500">{t("future.now")}</span>
                  <span className="text-xs text-zinc-500">50</span>
                  <span className="text-xs text-zinc-500">100</span>
                  <span className="text-xs text-zinc-500">150</span>
                  <span className="text-xs text-zinc-500">200 {t("future.years")}</span>
                </div>
              </div>
              
              {/* Scenario Type Selector - Only shown when future is selected */}
              {yearsIntoFuture > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-zinc-400 mr-2">{t("future.scenario")}:</span>
                  {["optimistic", "normal", "pessimistic"].map((type) => (
                    <button
                      key={type}
                      onClick={() => setScenarioType(type)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        scenarioType === type
                          ? type === "optimistic" 
                            ? "bg-green-500/20 text-green-400 border border-green-500/50"
                            : type === "pessimistic"
                            ? "bg-red-500/20 text-red-400 border border-red-500/50"
                            : "bg-yellow-500/20 text-yellow-400 border border-yellow-500/50"
                          : "bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-zinc-500"
                      }`}
                    >
                      {t(`future.${type}`)}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <div className="glass rounded-2xl p-2 flex gap-2">
              <label className="cursor-pointer">
                <input type="file" accept="image/*,.pdf" onChange={handleFileChange} disabled={isQuerying} className="hidden" />
                <Button type="button" variant="ghost" size="icon" disabled={isQuerying} className="h-12 w-12 text-zinc-400 hover:text-white" asChild>
                  <div><Paperclip className="w-5 h-5" /></div>
                </Button>
              </label>
              
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={yearsIntoFuture > 0 ? `${t("dashboard.askQuestion")} (${new Date().getFullYear() + yearsIntoFuture})` : t("dashboard.askQuestion")}
                disabled={isQuerying}
                className="flex-1 bg-transparent border-0 focus-visible:ring-0 h-12 text-white placeholder:text-zinc-500"
              />
              <Button type="submit" disabled={isQuerying || !query.trim()} className="bg-white text-black hover:bg-zinc-200 rounded-xl px-6 h-12">
                {isQuerying ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" />{t("dashboard.query")}</>}
              </Button>
            </div>
          </form>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-zinc-900/50 border border-white/5 p-1 rounded-xl">
            <TabsTrigger value="personas" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-black">
              <Users className="w-4 h-4 mr-2" />
              {t("dashboard.personas")} ({personas.length})
            </TabsTrigger>
            <TabsTrigger value="results" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-black">
              <MessageSquare className="w-4 h-4 mr-2" />
              {t("dashboard.queryResults")} ({queryResults.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="personas" className="mt-6">
            <div className="bento-grid">
              {personas.map((persona, index) => (
                <PersonaCard
                  key={persona.id}
                  persona={persona}
                  index={index}
                  onClick={() => handlePersonaClick(persona)}
                  isThinking={isQuerying}
                />
              ))}
              
              {/* Show skeletons for remaining personas */}
              {personas.length < totalExpected && 
                Array.from({ length: totalExpected - personas.length }).map((_, index) => (
                  <PersonaSkeleton 
                    key={`skeleton-${personas.length + index}`} 
                    index={personas.length + index}
                    totalCount={totalExpected}
                  />
                ))
              }
              
              {personas.length === 0 && totalExpected === 0 && (
                <div className="col-span-full text-center py-12">
                  <Brain className="w-12 h-12 text-yellow-400 mx-auto mb-4 animate-pulse" />
                  <p className="text-zinc-400 text-lg">{language === 'de' ? 'Personas werden generiert...' : 'Generating personas...'}</p>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="results" className="mt-6">
            {queryResults.length === 0 ? (
              <div className="text-center py-12 glass rounded-2xl">
                <MessageSquare className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400">No queries yet</p>
              </div>
            ) : (
              <div className="space-y-6">
                {queryResults.map((result, index) => (
                  <QueryResultView 
                    key={result.id} 
                    result={result} 
                    personas={personas} 
                    index={index}
                    onPersonaClick={handlePersonaClick}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Persona Detail Modal */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedPersona && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-yellow-500 to-amber-500 overflow-hidden flex items-center justify-center">
                    {selectedPersona.avatar_base64 ? (
                      <img
                        src={`data:image/jpeg;base64,${selectedPersona.avatar_base64}`}
                        alt={selectedPersona.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="font-heading font-bold text-white text-xl">
                        {selectedPersona.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                      </span>
                    )}
                  </div>
                  <div>
                    <h2 className="font-heading text-xl text-white">{selectedPersona.name}</h2>
                    <p className="text-sm text-zinc-400">{selectedPersona.age} {t("setup.yearsOld")} • {selectedPersona.gender}</p>
                  </div>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6 mt-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2 text-zinc-300">
                    <Briefcase className="w-4 h-4 text-yellow-400" />
                    <span>{selectedPersona.occupation}</span>
                  </div>
                  <div className="flex items-center gap-2 text-zinc-300">
                    <MapPin className="w-4 h-4 text-amber-400" />
                    <span>{selectedPersona.location}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {selectedPersona.education_level && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                      <GraduationCap className="w-4 h-4 text-emerald-400" />
                      <div>
                        <span className="text-xs text-emerald-400/70">{t("persona.education")}</span>
                        <p className="text-sm text-emerald-300">{getTranslatedValue('education', selectedPersona.education_level)}</p>
                      </div>
                    </div>
                  )}
                  {selectedPersona.intelligence_level && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                      <Lightbulb className="w-4 h-4 text-purple-400" />
                      <div>
                        <span className="text-xs text-purple-400/70">{t("persona.intelligence")}</span>
                        <p className="text-sm text-purple-300">{getTranslatedValue('intelligence', selectedPersona.intelligence_level)}</p>
                      </div>
                    </div>
                  )}
                  {selectedPersona.family_situation && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-pink-500/10 border border-pink-500/20 rounded-lg">
                      <Home className="w-4 h-4 text-pink-400" />
                      <div>
                        <span className="text-xs text-pink-400/70">{t("persona.family")}</span>
                        <p className="text-sm text-pink-300">{getTranslatedValue('family', selectedPersona.family_situation)}</p>
                      </div>
                    </div>
                  )}
                  {selectedPersona.household_income && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                      <DollarSign className="w-4 h-4 text-amber-400" />
                      <div>
                        <span className="text-xs text-amber-400/70">{t("persona.income")}</span>
                        <p className="text-sm text-amber-300">{getTranslatedValue('income', selectedPersona.household_income)}</p>
                      </div>
                    </div>
                  )}
                </div>

                <div><p className="text-zinc-300 leading-relaxed">{selectedPersona.description}</p></div>

                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Brain className="w-4 h-4 text-yellow-400" />
                    <span className="text-sm font-medium text-white">{t("persona.personalityTraits")}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedPersona.personality_traits.map((trait) => (
                      <span key={trait} className="px-3 py-1 bg-yellow-500/10 border border-yellow-500/20 rounded-full text-sm text-yellow-300">{trait}</span>
                    ))}
                  </div>
                </div>

                <div>
                  <span className="text-sm font-medium text-white block mb-3">{t("persona.interests")}</span>
                  <div className="flex flex-wrap gap-2">
                    {selectedPersona.interests.map((interest) => (
                      <span key={interest} className="px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-sm text-emerald-300">{interest}</span>
                    ))}
                  </div>
                </div>

                <div>
                  <span className="text-sm font-medium text-white block mb-3">{t("persona.values")}</span>
                  <div className="flex flex-wrap gap-2">
                    {selectedPersona.values.map((value) => (
                      <span key={value} className="px-3 py-1 bg-blue-500/10 border border-blue-500/20 rounded-full text-sm text-blue-300">{value}</span>
                    ))}
                  </div>
                </div>

                <div>
                  <span className="text-sm font-medium text-white block mb-3">{t("persona.painPoints")}</span>
                  <ul className="space-y-2">
                    {selectedPersona.pain_points.map((point, i) => (
                      <li key={i} className="text-zinc-400 text-sm">• {point}</li>
                    ))}
                  </ul>
                </div>

                <div>
                  <span className="text-sm font-medium text-white block mb-3">{t("persona.goals")}</span>
                  <ul className="space-y-2">
                    {selectedPersona.goals.map((goal, i) => (
                      <li key={i} className="text-zinc-400 text-sm">• {goal}</li>
                    ))}
                  </ul>
                </div>

                {selectedPersona.buying_behavior && (
                  <div>
                    <span className="text-sm font-medium text-white block mb-3">{t("persona.buyingBehavior")}</span>
                    <p className="text-zinc-400 text-sm leading-relaxed">{selectedPersona.buying_behavior}</p>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
